import { Injectable, Injector, Type, NgZone, inject } from '@angular/core';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ModalContainerComponent } from '../../components/modals/modal-container.component';
import { ModalRef } from './modal-ref';
import { ModalConfig, MODAL_DATA } from './modal-config';

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  private readonly _overlay = inject(Overlay);
  private readonly _injector = inject(Injector);
  private readonly _ngZone = inject(NgZone);

  /**
   * Open a component inside a modal overlay.
   * Returns a ModalRef that can be used to close the modal and listen for results.
   */
  open<T, D = any, R = any>(
    component: Type<T>,
    config?: ModalConfig<D>
  ): ModalRef<T, R> {
    const cfg = config || {};

    // Build panel class list
    const panelClasses = ['app-modal-overlay-pane'];
    if (cfg.panelClass) {
      const classes = Array.isArray(cfg.panelClass) ? cfg.panelClass : [cfg.panelClass];
      panelClasses.push(...classes);
    }

    // Create the CDK overlay
    const overlayConfig = new OverlayConfig({
      hasBackdrop: cfg.hasBackdrop !== false,
      backdropClass: 'app-modal-backdrop',
      panelClass: panelClasses,
      positionStrategy: this._overlay.position().global().centerHorizontally().centerVertically(),
      scrollStrategy: this._overlay.scrollStrategies.block(),
    });

    const overlayRef = this._overlay.create(overlayConfig);
    const modalRef = new ModalRef<T, R>(overlayRef, cfg.disableClose || false);

    // Handle backdrop clicks
    if (overlayConfig.hasBackdrop) {
      overlayRef.backdropClick().subscribe((event) => {
        modalRef._emitBackdropClick(event);
        if (!cfg.disableClose) {
          modalRef.close();
        }
      });
    }

    // Create the container component
    const containerPortal = new ComponentPortal(ModalContainerComponent);
    const containerRef = overlayRef.attach(containerPortal);
    const containerInstance = containerRef.instance;
    containerInstance.modalRef = modalRef;

    // Create a custom injector for the content component
    // Provides both new (ModalRef/MODAL_DATA) and legacy (MatDialogRef/MAT_DIALOG_DATA)
    // tokens so existing dialog components work without changes during migration.
    const contentInjector = Injector.create({
      parent: this._injector,
      providers: [
        { provide: ModalRef, useValue: modalRef },
        { provide: MODAL_DATA, useValue: cfg.data || null },
        // Backward compatibility — remove these once all dialogs are migrated
        { provide: MatDialogRef, useValue: modalRef },
        { provide: MAT_DIALOG_DATA, useValue: cfg.data || null },
      ],
    });

    // Attach the content component inside the container's ViewContainerRef
    // Wait for AfterViewInit so contentRef is guaranteed to be available,
    // even when open() is called from inside an afterClosed() callback.
    containerInstance.ready$.subscribe(() => {
      this._ngZone.run(() => {
        const contentRef = containerInstance.contentRef.createComponent(component, {
          injector: contentInjector,
        });
        modalRef.componentInstance = contentRef.instance;

        // Provide a centralized styling hook for all modal content components.
        const modalContentElement = contentRef.location.nativeElement as HTMLElement;
        modalContentElement.classList.add('modal-content-root');

        // Auto-focus management
        if (cfg.autoFocus !== false) {
          const focusable = modalContentElement.querySelector<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          focusable?.focus();
        }
      });
    });

    return modalRef;
  }
}
